import React from "react"
import { Icon } from "components"
import Strings from "utils/strings"
import { FleetVehicle } from "../types"
import { formatDistance } from "../utils"

interface FleetStatsBarProps {
	vehicles: FleetVehicle[]
}

function formatDuration(millis: number): string {
	const totalMinutes = Math.round(millis / 60000)
	if (totalMinutes < 60) return `${totalMinutes} min`
	const hours = Math.floor(totalMinutes / 60)
	const minutes = totalMinutes % 60
	return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`
}

interface StatCard {
	label: string
	value: string | number
	subtitle: string
	gradient: string
	icon: string
}

const FleetStatsBar: React.FC<FleetStatsBarProps> = ({ vehicles }) => {
	const s = Strings.fleetDashboard

	const totalVehicles = vehicles.length
	const movingVehicles = vehicles.filter((v) => (v.speed ?? 0) > 0)
	const activeVehicles = movingVehicles.length
	const idleVehicles = totalVehicles - activeVehicles

	const totalRemainingStops = vehicles.reduce(
		(sum, v) => sum + (v.remainingVehicleJourneySegments?.length ?? 0),
		0
	)

	const vehiclesWithNextDist = vehicles.filter(
		(v) => (v.remainingDistanceMeters ?? 0) > 0
	)
	const avgNextStopDistance =
		vehiclesWithNextDist.length > 0
			? vehiclesWithNextDist.reduce(
					(sum, v) => sum + (v.remainingDistanceMeters ?? 0),
					0
				) / vehiclesWithNextDist.length
			: null

	const vehiclesWithNextDur = vehicles.filter(
		(v) => (v.remainingDurationMillis ?? 0) > 0
	)
	const avgNextStopDuration =
		vehiclesWithNextDur.length > 0
			? vehiclesWithNextDur.reduce(
					(sum, v) => sum + (v.remainingDurationMillis ?? 0),
					0
				) / vehiclesWithNextDur.length
			: null

	const avgSpeed =
		activeVehicles > 0
			? Math.round(
					movingVehicles.reduce((sum, v) => sum + (v.speed ?? 0), 0) /
						activeVehicles
				)
			: null

	const isSingle = vehicles.length === 1
	const nextSubtitle = isSingle ? s.toNextStop : s.avgPerVehicle

	const secondCard: StatCard = isSingle
		? {
				label: s.remainingStops,
				value: totalRemainingStops > 0 ? totalRemainingStops : "—",
				subtitle: s.thisVehicle,
				gradient: "linear-gradient(274.42deg, #994227 0%, #fa7348 124.45%)",
				icon: "location",
			}
		: {
				label: s.avgSpeed,
				value: avgSpeed != null ? `${avgSpeed} km/h` : "—",
				subtitle: `${activeVehicles} ${s.movingVehicles}`,
				gradient: "linear-gradient(274.42deg, #994227 0%, #fa7348 124.45%)",
				icon: "velocimetro",
			}

	const cards: StatCard[] = [
		{
			label: s.totalVehicles,
			value: totalVehicles,
			subtitle: `${activeVehicles} ${s.onRoute} · ${idleVehicles} ${s.idle.toLowerCase()}`,
			gradient: "linear-gradient(135deg, #075aae 0%, #1a7fd4 100%)",
			icon: "delivery-truck",
		},
		secondCard,
		{
			label: s.nextStop,
			value:
				avgNextStopDistance != null ? formatDistance(avgNextStopDistance) : "—",
			subtitle: nextSubtitle,
			gradient: "linear-gradient(90deg, #37674d 0%, #6eab87 100%)",
			icon: "route",
		},
		{
			label: s.estimatedTime,
			value:
				avgNextStopDuration != null ? formatDuration(avgNextStopDuration) : "—",
			subtitle: nextSubtitle,
			gradient: "linear-gradient(135deg, #163683 0%, #2a52b8 100%)",
			icon: "clock1",
		},
	]

	return (
		<div
			style={{
				display: "flex",
				gap: 16,
				paddingBottom: 16,
				flexWrap: "wrap",
			}}
		>
			{cards.map((card) => (
				<div
					key={card.label}
					style={{
						flex: "1 1 160px",
						minWidth: 160,
						backgroundColor: "white",
						borderRadius: 8,
						padding: "20px",
						boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
						display: "flex",
						alignItems: "center",
						gap: 16,
					}}
				>
					<div
						style={{
							width: 48,
							height: 48,
							minWidth: 48,
							borderRadius: "50%",
							background: card.gradient,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Icon name={card.icon} style={{ fontSize: 20, color: "white" }} />
					</div>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 2,
							minWidth: 0,
						}}
					>
						<span
							style={{
								fontSize: 12,
								color: "#8e9aab",
								fontWeight: 500,
								whiteSpace: "nowrap",
							}}
						>
							{card.label}
						</span>
						<span
							style={{
								fontSize: 22,
								fontWeight: 700,
								color: "#103246",
								lineHeight: 1.1,
							}}
						>
							{card.value}
						</span>
						<span
							style={{
								fontSize: 11,
								color: "#b0bac4",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{card.subtitle}
						</span>
					</div>
				</div>
			))}
		</div>
	)
}

export default FleetStatsBar
